/**
 * The confirm-modal primitive — ONE consistent surface for every
 * irreversible action (PO ruling, 2026-07-18; test-spec §5.2's three-layer
 * precedent, layer 2: the client always confirms, and the confirm names any
 * fact the actor cannot otherwise see).
 *
 * Never `window.confirm`: a native dialog can't name consequences richly,
 * can't be asserted through the DOM mirror, and isn't stylable or reliably
 * accessible. Every §5.2 confirm goes through here so the affordance stays
 * consistent — callers supply their own testids so each dialog keeps its
 * pinned E2E surface. Current callers: removeSeat (reference
 * implementation); upcoming: last-actor timeline removal, token deletion.
 *
 * Behaviour contract (asserted by the removeSeat suite):
 *  - Cancel first in the DOM and focused — the safe default;
 *  - Escape cancels; Tab/Shift+Tab toggle between the two buttons and never
 *    leave the modal (aria-modal, enforced);
 *  - resolving either way clears the mount before the callback fires.
 */

export interface ConfirmModalWarning {
  /** data-testid for this warning line (e.g. "reconnecting-warning"). */
  testid: string;
  text: string;
}

export interface ConfirmModalSpec {
  /** data-testid of the dialog element (e.g. "remove-seat-confirm"). */
  testid: string;
  /** Extra data-* attributes on the dialog, camelCase keys (seatId → data-seat-id). */
  data?: Record<string, string>;
  title: string;
  body: string;
  /** The §5.2 facts the actor cannot otherwise see; each renders as its own
   *  role="alert" line above the actions. */
  warnings?: readonly ConfirmModalWarning[];
  confirmTestid: string;
  confirmLabel: string;
  cancelTestid: string;
  /** Defaults to "Cancel". */
  cancelLabel?: string;
}

export interface ConfirmModalCallbacks {
  onConfirm(): void;
  onCancel?: (() => void) | undefined;
}

/**
 * Render the confirm modal into `root` (a modal mount), replacing whatever
 * was there. Confirm/cancel/Escape all clear `root` before their callback.
 */
export function renderConfirmModal(
  root: HTMLElement,
  spec: ConfirmModalSpec,
  cb: ConfirmModalCallbacks,
): void {
  root.replaceChildren();

  const dialog = document.createElement("div");
  dialog.dataset["testid"] = spec.testid;
  for (const [key, value] of Object.entries(spec.data ?? {})) {
    dialog.dataset[key] = value;
  }
  dialog.setAttribute("role", "dialog");
  dialog.setAttribute("aria-modal", "true");
  dialog.setAttribute("aria-labelledby", `${spec.testid}-title`);
  dialog.setAttribute("aria-describedby", `${spec.testid}-body`);

  const title = document.createElement("h2");
  title.id = `${spec.testid}-title`;
  title.textContent = spec.title;
  dialog.appendChild(title);

  const body = document.createElement("p");
  body.id = `${spec.testid}-body`;
  body.textContent = spec.body;
  dialog.appendChild(body);

  for (const warning of spec.warnings ?? []) {
    const line = document.createElement("p");
    line.dataset["testid"] = warning.testid;
    line.setAttribute("role", "alert");
    line.textContent = warning.text;
    dialog.appendChild(line);
  }

  const actions = document.createElement("div");
  actions.className = "dialog-actions";

  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.dataset["testid"] = spec.cancelTestid;
  cancel.textContent = spec.cancelLabel ?? "Cancel";

  const confirm = document.createElement("button");
  confirm.type = "button";
  confirm.dataset["testid"] = spec.confirmTestid;
  confirm.textContent = spec.confirmLabel;

  const close = (): void => root.replaceChildren();
  cancel.addEventListener("click", () => {
    close();
    cb.onCancel?.();
  });
  confirm.addEventListener("click", () => {
    close();
    cb.onConfirm();
  });
  dialog.addEventListener("keydown", (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      close();
      cb.onCancel?.();
      return;
    }
    // Focus trap: Tab never escapes the modal (aria-modal promises this).
    // With exactly two focusable elements, Tab and Shift+Tab both toggle.
    if (event.key === "Tab") {
      event.preventDefault();
      (document.activeElement === confirm ? cancel : confirm).focus();
    }
  });

  // Cancel first in the DOM and focused: an irreversible action defaults away
  // from the destructive button.
  actions.append(cancel, confirm);
  dialog.appendChild(actions);
  root.appendChild(dialog);
  cancel.focus();
}
